import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, SyntheticBanner, InfoNote } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, FlaskConical } from "lucide-react";

const Field = ({ label, children, required }) => (
  <div className="space-y-1.5">
    <Label className="text-xs">{label}{required && <span className="text-amber-600"> *</span>}</Label>
    {children}
  </div>
);

export default function OrderIntake() {
  const { bootstrap, roleCode } = useApp();
  const nav = useNavigate();
  const [cases, setCases] = useState([]);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    patient_name: "", dob_demo: "", synthetic_patient_id: "", provider_id: "",
    test_id: "test-rpp", cpt_demo: "DEMO-CPT-87999", diagnosis_demo: "", payer_id: "payer-medicare",
    plan: "Medicare Part B — Demo", jurisdiction: "Demonstration Jurisdiction — Northeast",
    service_date: "2026-06-05", clinical_indication_field: "", prior_testing: "",
    authorization_status: "Not required (demo)", order_notes: "", note: "",
  });

  useEffect(() => {
    api.cases().then(setCases).catch(() => {});
    if (bootstrap?.jurisdictions?.[0]) setF((p) => ({ ...p, jurisdiction: bootstrap.jurisdictions[0] }));
  }, [bootstrap]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const loadCase = (id) => {
    const c = cases.find((x) => x.id === id);
    if (!c) return;
    setF((p) => ({
      ...p, patient_name: c.patient_name, dob_demo: c.dob_demo,
      synthetic_patient_id: c.synthetic_patient_id, provider_id: c.provider_id,
      test_id: c.test_id, cpt_demo: c.cpt_demo, diagnosis_demo: c.diagnosis_demo,
      payer_id: c.payer_id, plan: c.plan, jurisdiction: c.jurisdiction,
      service_date: c.service_date, clinical_indication_field: c.clinical_indication_field,
      prior_testing: c.prior_testing, authorization_status: c.authorization_status,
      order_notes: c.order_notes, note: c.note, case_id: c.id,
    }));
    toast.success(`Loaded synthetic case: ${c.patient_name}`);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const payload = f.case_id
        ? { case_id: f.case_id, role_code: roleCode }
        : { order: f, role_code: roleCode };
      const order = await api.createOrder(payload);
      toast.info("Order submitted — running evaluation…");
      await api.evaluate(order.id, { role_code: roleCode });
      toast.success("Readiness evaluation complete");
      nav(`/orders/${order.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Evaluation failed");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader testid="intake-header" title="Order Intake & Readiness Evaluation"
        subtitle="Submit an electronic laboratory order and progress note to evaluate reimbursement readiness before testing." />
      <SyntheticBanner />

      <Card className="mb-4 border-teal-200 bg-teal-50/40 p-4">
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-teal-900">
          <FlaskConical className="h-4 w-4" /> Quick-load a synthetic demonstration case
        </div>
        <div className="flex flex-wrap gap-2">
          {cases.map((c) => (
            <Button key={c.id} variant="outline" size="sm" data-testid={`load-case-${c.id}`}
              className="border-teal-300 bg-white text-xs" onClick={() => loadCase(c.id)}>
              {c.patient_name}
            </Button>
          ))}
        </div>
      </Card>

      <Card className="p-5">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <Field label="Patient name" required>
            <Input data-testid="in-patient-name" value={f.patient_name} onChange={(e) => set("patient_name", e.target.value)} />
          </Field>
          <Field label="Date of birth (demo)">
            <Input data-testid="in-dob" value={f.dob_demo} onChange={(e) => set("dob_demo", e.target.value)} placeholder="YYYY-MM-DD" />
          </Field>
          <Field label="Synthetic patient ID" required>
            <Input data-testid="in-patient-id" value={f.synthetic_patient_id} onChange={(e) => set("synthetic_patient_id", e.target.value)} />
          </Field>

          <Field label="Ordering provider / practice" required>
            <Select value={f.provider_id} onValueChange={(v) => set("provider_id", v)}>
              <SelectTrigger data-testid="in-provider"><SelectValue placeholder="Select provider" /></SelectTrigger>
              <SelectContent>{bootstrap?.providers?.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Test" required>
            <Select value={f.test_id} onValueChange={(v) => set("test_id", v)}>
              <SelectTrigger data-testid="in-test"><SelectValue /></SelectTrigger>
              <SelectContent>{bootstrap?.tests?.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="CPT / HCPCS (demo)">
            <Input data-testid="in-cpt" value={f.cpt_demo} onChange={(e) => set("cpt_demo", e.target.value)} />
          </Field>

          <Field label="ICD-10 diagnosis (demo)" required>
            <Input data-testid="in-icd" value={f.diagnosis_demo} onChange={(e) => set("diagnosis_demo", e.target.value)} placeholder="e.g. J06.9 (demo)" />
          </Field>
          <Field label="Payer" required>
            <Select value={f.payer_id} onValueChange={(v) => set("payer_id", v)}>
              <SelectTrigger data-testid="in-payer"><SelectValue /></SelectTrigger>
              <SelectContent>{bootstrap?.payers?.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Plan">
            <Input data-testid="in-plan" value={f.plan} onChange={(e) => set("plan", e.target.value)} />
          </Field>

          <Field label="Jurisdiction" required>
            <Input data-testid="in-jurisdiction" value={f.jurisdiction} onChange={(e) => set("jurisdiction", e.target.value)} />
          </Field>
          <Field label="Date of service" required>
            <Input data-testid="in-service-date" value={f.service_date} onChange={(e) => set("service_date", e.target.value)} placeholder="YYYY-MM-DD" />
          </Field>
          <Field label="Authorization status">
            <Input data-testid="in-auth" value={f.authorization_status} onChange={(e) => set("authorization_status", e.target.value)} />
          </Field>

          <Field label="Clinical indication">
            <Input data-testid="in-indication" value={f.clinical_indication_field} onChange={(e) => set("clinical_indication_field", e.target.value)} />
          </Field>
          <Field label="Prior testing">
            <Input data-testid="in-prior" value={f.prior_testing} onChange={(e) => set("prior_testing", e.target.value)} />
          </Field>
          <Field label="Order notes">
            <Input data-testid="in-order-notes" value={f.order_notes} onChange={(e) => set("order_notes", e.target.value)} />
          </Field>
        </div>

        <div className="mt-4">
          <Field label="Physician progress note (clinical documentation)" required>
            <Textarea data-testid="in-note" rows={6} value={f.note}
              onChange={(e) => { set("note", e.target.value); set("case_id", undefined); }}
              placeholder="Paste the synthetic physician progress note here…" />
          </Field>
          <InfoNote className="mt-2">
            RequiSure will extract only facts explicitly present in this note. It will never invent
            symptoms, duration, diagnoses or medical necessity. Missing information is reported as
            “Not found in submitted documentation.”
          </InfoNote>
        </div>

        <div className="mt-5 flex justify-end">
          <Button data-testid="evaluate-readiness-btn" disabled={busy}
            className="bg-slate-900 px-6 hover:bg-slate-800" onClick={submit}>
            {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Evaluating…</> : "Evaluate Readiness"}
          </Button>
        </div>
      </Card>
    </div>
  );
}
